import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Search, Shield } from 'lucide-react';
import { AgentPreviewEndDrawer } from '../components/AgentPreviewEndDrawer';
import { PublicAgentsBrowse } from '../components/PublicAgentsBrowse';
import { BRAND_LOGO_PNG } from '../brandAssets';
import { useAppStore } from '../store/useAppStore';
import { isStaffRole } from '../types/roles';

export function HomePage() {
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);
  const userRole = useAppStore((s) => s.userRole);
  const logout = useAppStore((s) => s.logout);
  const canSeeAdmin = isStaffRole(userRole);
  const [headerSignInOpen, setHeaderSignInOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  return (
    <div className="relative w-full max-w-[100vw] bg-transparent text-th-primary">
      <AgentPreviewEndDrawer
        open={headerSignInOpen}
        onClose={() => setHeaderSignInOpen(false)}
        signInOnly
        agent={null}
      />
      <header className="sticky top-0 z-20 border-b border-subtle bg-surface/80 backdrop-blur">
        {/* Mobile: search only; desktop: full top bar */}
        <div className="mx-auto max-w-6xl px-4 py-2 md:py-4">
          <div className="md:hidden">
            <div className="flex w-full min-w-0 flex-col gap-2">
              <div className="relative w-full min-w-0">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-th-tertiary" />
                <input
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search name, tags, categories…"
                  title="Match all words (e.g. gst india). Searches titles, descriptions, tags, and categories."
                  autoComplete="off"
                  enterKeyHint="search"
                  maxLength={500}
                  className="w-full rounded-xl border border-subtle bg-elevated py-2.5 pl-10 pr-4 text-sm text-th-primary placeholder:text-th-tertiary focus:border-brand/50 focus:outline-none focus:ring-2 focus:ring-brand/20"
                  aria-label="Search agents by name, description, tags, or category"
                />
              </div>
              {!isAuthenticated ? (
                <button
                  type="button"
                  onClick={() => setHeaderSignInOpen(true)}
                  className="w-full rounded-lg bg-brand py-2 text-center text-sm font-medium text-surface hover:bg-brand-hover"
                >
                  Get started
                </button>
              ) : null}
            </div>
          </div>

          <div className="hidden items-center justify-between gap-3 md:flex">
            <Link
              to="/"
              className="flex min-w-0 shrink-0 items-center"
              aria-label="Hyper App Store home"
            >
              <img
                src={BRAND_LOGO_PNG}
                alt="Hyper App Store"
                className="h-8 max-h-9 w-auto max-w-[min(46vw,220px)] object-contain object-left"
                decoding="async"
              />
            </Link>

            <div className="relative min-w-0 max-w-md flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-th-tertiary" />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search name, tags, categories…"
                title="Match all words (e.g. gst india). Searches titles, descriptions, tags, and categories."
                autoComplete="off"
                enterKeyHint="search"
                maxLength={500}
                className="w-full rounded-xl border border-subtle bg-elevated py-2.5 pl-10 pr-4 text-sm text-th-primary placeholder:text-th-tertiary focus:border-brand/50 focus:outline-none focus:ring-2 focus:ring-brand/20"
                aria-label="Search agents by name, description, tags, or category"
              />
            </div>

            <div className="flex shrink-0 items-center gap-2 sm:gap-4">
              <nav className="flex items-center gap-2 text-sm sm:gap-4">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/library"
                      className="rounded-lg bg-brand px-3 py-1.5 font-medium text-surface hover:bg-brand-hover"
                    >
                      Library
                    </Link>
                    <Link
                      to="/profile"
                      className="rounded-lg px-3 py-1.5 font-medium text-th-secondary transition hover:text-th-primary"
                    >
                      Profile
                    </Link>
                    <button
                      type="button"
                      onClick={() => void logout()}
                      className="inline-flex items-center gap-1.5 text-th-secondary hover:text-th-primary"
                    >
                      <LogOut className="h-4 w-4" />
                      Log out
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setHeaderSignInOpen(true)}
                    className="rounded-lg bg-brand px-3 py-1.5 font-medium text-surface hover:bg-brand-hover"
                  >
                    Get started
                  </button>
                )}
                {canSeeAdmin && (
                  <Link
                    to="/admin"
                    className="inline-flex items-center gap-1 text-th-tertiary hover:text-th-secondary"
                  >
                    <Shield className="h-4 w-4" />
                    Admin
                  </Link>
                )}
              </nav>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 pb-12 pt-8 max-md:pb-10 md:pb-16">
        <section aria-label="Public agents">
          <h2 className="sr-only">All active public agents</h2>
          <PublicAgentsBrowse searchInput={searchInput} />
        </section>
      </main>
    </div>
  );
}
